import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import SmallHero from '../components/SmallHero';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import '../styles/animations.css';

const Highlights: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [imageError, setImageError] = useState<Set<number>>(new Set());

  // Generate image paths - you have 11 images (1.png to 11.png)
  const highlights = Array.from({ length: 10 }, (_, i) => ({
    id: i + 1,
    src: `/highlights-pictures/${i + 1}.png`,
    alt: `Session Highlight ${i + 1}`,
    title: `Featured Session ${i + 1}`
  }));

  const openLightbox = (index: number) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedImage === null) return;
    
    const newIndex = direction === 'next' 
      ? (selectedImage + 1) % highlights.length
      : (selectedImage - 1 + highlights.length) % highlights.length;
    
    setSelectedImage(newIndex);
  };

  const handleImageError = (id: number) => {
    setImageError(prev => new Set(prev).add(id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') navigateImage('prev');
    if (e.key === 'ArrowRight') navigateImage('next');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Navigation />
      <SmallHero 
        title="Session Highlights" 
        subtitle="Discover the exciting panel sessions and workshops at Language Intelligence 2025"
      />
      
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Introduction */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center p-3 bg-gradient-to-r from-[#00529B]/10 to-[#E31D3C]/10 rounded-full mb-4">
            <SparklesIcon className="h-8 w-8 text-[#00529B]" />
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore our featured sessions and special events. Click on any image to view it in full size.
          </p>
          <div className="mt-4">
            <a 
              href="/schedule" 
              className="inline-flex items-center text-[#00529B] hover:text-[#003d73] font-medium transition-colors duration-200"
            >
              Browse the our complete programme schedule
              <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-delay-1">
          {highlights.map((highlight, index) => (
            !imageError.has(highlight.id) && (
              <div
                key={highlight.id}
                className="group cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] aspect-video">
                  <img
                    src={highlight.src}
                    alt={highlight.alt}
                    onError={() => handleImageError(highlight.id)}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white font-semibold text-lg">{highlight.title}</p>
                      <p className="text-white/80 text-sm">Click to view full size</p>
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center animate-fade-in-delay-2">
          <div className="bg-gradient-to-r from-[#00529B] to-[#003D74] rounded-2xl p-8 md:p-12 shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Join Us at Language Intelligence 2025
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Don't miss out on these incredible sessions and networking opportunities. 
              Register today to secure your spot!
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="https://www.eventbrite.co.uk/e/language-intelligence-2025-tickets-1299678576609"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 bg-white text-[#00529B] font-semibold rounded-full hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
              >
                Register Now
              </a>
              <a
                href="/schedule"
                className="inline-block px-8 py-3 bg-[#E31D3C] text-white font-semibold rounded-full hover:bg-[#C41833] transition-colors shadow-lg hover:shadow-xl"
              >
                View Full Schedule
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            aria-label="Close"
          >
            <XMarkIcon className="h-8 w-8 text-white" />
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            aria-label="Previous image"
          >
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Next button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            aria-label="Next image"
          >
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
            {selectedImage + 1} / {highlights.length}
          </div>

          {/* Main image */}
          <div 
            className="max-w-7xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={highlights[selectedImage].src}
              alt={highlights[selectedImage].alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Highlights;
